import type { RowSelectionState, ColumnDef } from '@tanstack/vue-table'
import TrashGreenIcon from '../TrashGreenIcon.vue'
import { Button } from '~/components/ui/button'
import { Checkbox } from '~/components/ui/checkbox'
import AlphabeticSortIcon from '~/components/AlphabeticSortIcon.vue'
import ExpandIcon from '~/components/ExpandIcon.vue'
import DataTableFilterableHeader from '~/components/ui/table/DataTableFilterableHeader.vue'
import EditGhgEmissionFactorModal from '~/components/EditGhgEmissionFactorModal.vue'

// format the emission factor name
const formatEmissionFactorName = (emissionFactor: PreconfiguredEmissionFactor) => {
  const name = emissionFactor.emission_factor.emission_factor

  // truncate if longer than 30 characters
  if (name.length > 30) {
    return `${name.substring(0, 27)}...`
  }

  return name
}

// Emission Factor column cell
const emissionFactorCell = ({ row }: { row: { original: PreconfiguredEmissionFactor } }) => {
  const emissionFactor = row.original
  const formattedName = formatEmissionFactorName(emissionFactor)

  return h('div', {
    class: 'font-medium text-sm overflow-hidden whitespace-nowrap text-ellipsis',
    title: emissionFactor.emission_factor.emission_factor,
  }, formattedName)
}

// helper to determine asset display info
const getAssetDisplayInfo = (emissionFactor: PreconfiguredEmissionFactor) => {
  // Special case: applied to all assets
  if (emissionFactor.is_applied_all_asset) {
    return { display: 'All Assets', showExpandIcon: false }
  }

  // Get assets count
  const count = emissionFactor.assets.length

  // Return appropriate display based on count
  switch (count) {
    case 0:
      return { display: 'No assets', showExpandIcon: false }
    case 1:
      return { display: emissionFactor.assets[0].name, showExpandIcon: false }
    default:
      return { display: `${count} assets`, showExpandIcon: true }
  }
}

// helper to determine reporting year display info
const getYearDisplayInfo = (emissionFactor: PreconfiguredEmissionFactor) => {
  // special case: applied to all reporting years
  if (emissionFactor.is_applied_all_reporting_year) {
    return { display: 'All Reporting Years', showExpandIcon: false }
  }

  const count = emissionFactor.reporting_years.length

  // return appropriate display based on count
  switch (count) {
    case 0:
      return { display: 'No years', showExpandIcon: false }
    case 1:
      return { display: emissionFactor.reporting_years[0].formatted_date, showExpandIcon: false }
    default:
      return { display: `${count} years`, showExpandIcon: true }
  }
}

interface FilterOption {
  id: string
  name: string
}

interface ReportingYear {
  start_date: string
  end_date: string
  formatted_date: string
  id?: string
}

interface EmissionFactorFilterOptions {
  item_descriptions: FilterOption[]
  measurements: FilterOption[]
  categories: FilterOption[]
  assets: FilterOption[]
  reporting_years: ReportingYear[]
}

interface ColumnProps {
  rowSelection: Ref<RowSelectionState>
  filterOptions: EmissionFactorFilterOptions
  showAssetsModal: (assets: PreconfiguredEmissionFactor['assets']) => void
  showReportingYearsModal: (years: PreconfiguredEmissionFactor['reporting_years']) => void
  refresh: () => void
  onDeleteRow?: (id: string) => void
}

export const createColumns = ({
  rowSelection,
  filterOptions,
  showAssetsModal,
  showReportingYearsModal,
  refresh,
  onDeleteRow,
}: ColumnProps) => {
  // modal/drawer for assets
  const createAssetCell = ({ row }: { row: { original: PreconfiguredEmissionFactor } }) => {
    const emissionFactor = row.original
    const { display, showExpandIcon } = getAssetDisplayInfo(emissionFactor)

    return h('div', { class: 'flex justify-between items-center' }, [
      h('span', { class: 'text-sm' }, display),

      // Only show expand button if needed
      showExpandIcon && h('button', {
        class: 'p-1 hover:bg-slate-200 rounded-md',
        onClick: () => showAssetsModal(emissionFactor.assets),
      }, [
        h(ExpandIcon, { class: 'h-4 w-4 text-main-primary' }),
      ]),
    ])
  }

  // modal/drawer for reporting years
  const createReportingYearCell = ({ row }: { row: { original: PreconfiguredEmissionFactor } }) => {
    const emissionFactor = row.original
    const { display, showExpandIcon } = getYearDisplayInfo(emissionFactor)

    return h('div', { class: 'flex justify-between items-center' }, [
      h('span', { class: 'text-sm' }, display),

      // Only show expand button if needed
      showExpandIcon && h('button', {
        class: 'p-1 hover:bg-slate-200 rounded-md',
        onClick: () => showReportingYearsModal(emissionFactor.reporting_years),
      }, [
        h(ExpandIcon, { class: 'h-4 w-4 text-main-primary' }),
      ]),
    ])
  }

  return [
    {
      id: 'select',
      header: () => {
        return h('div', { class: 'checkbox-header' }, '')
      },
      cell: ({ row }) => {
        return h('div', { class: 'px-1' }, [
          h(Checkbox, {
            'checked': rowSelection.value[row.original.id] ?? false,
            'onUpdate:checked': (value: boolean) => {
              const rowId = row.original.id
              if (value) {
                rowSelection.value = { ...rowSelection.value, [rowId]: true }
              }
              else {
                const { [rowId]: removed, ...rest } = rowSelection.value
                rowSelection.value = rest
              }
              row.toggleSelected(!!value)
            },
          }),
        ])
      },
      enableSorting: false,
      enableColumnFilter: false,
      size: 40,
    },
    {
      accessorKey: 'emission_factor',
      id: 'emission_factor',
      size: 250,
      header: ({ column }) => {
        const isSorted = column.getIsSorted()
        const sortIconStyling = isSorted === 'asc' ? 'text-green-500' : isSorted === 'desc' ? 'text-red-500 rotate-180' : ''

        return h('div', { class: 'flex justify-between items-center' }, [
          h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'Emission Factor'),
          h(Button, {
            class: 'text-main-primary',
            variant: 'ghost',
            onClick: () => column.toggleSorting(isSorted === 'asc', true),
          }, () => h(AlphabeticSortIcon, { customStyle: `size-5 ${sortIconStyling}` })),
        ])
      },
      cell: emissionFactorCell,
      enableSorting: true,
    },
    {
      accessorKey: 'value',
      id: 'value',
      size: 200,
      header: () => {
        return h('div', { class: 'flex justify-between items-center' }, [
          h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'Value'),
        ])
      },
      cell: ({ row }) => {
        const emissionFactor = row.original
        const value = emissionFactor.emission_factor.derived_ef_value.toFixed(4)
        const unit = emissionFactor.emission_factor.unit

        return h('div', { class: 'text-sm' }, `${value} kgCO2e/${unit}`)
      },
      enableSorting: true,
    },
    {
      accessorKey: 'item_description',
      id: 'item_description',
      size: 200,
      header: ({ column }) => {
        return h(DataTableFilterableHeader, {
          column,
          title: 'Item Description',
          filterData: (filterOptions.item_descriptions || []),
        })
      },
      cell: ({ row }) => h('div', { class: 'text-sm' }, row.original.item_description),
      enableColumnFilter: true,
    },
    {
      accessorKey: 'measurement_group',
      id: 'measurement_group',
      size: 200,
      header: () => {
        return h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'Measurement Groups')
      },
      cell: ({ row }) => h('div', { class: 'text-sm' }, row.original.measurement.name),
      enableColumnFilter: true,
    },
    {
      accessorKey: 'assets',
      id: 'assets',
      size: 200,
      header: ({ column }) => {
        return h(DataTableFilterableHeader, {
          column,
          title: 'Asset',
          filterData: (filterOptions.assets || []),
        })
      },
      cell: createAssetCell,
      enableSorting: false,
      enableColumnFilter: true,
    },
    {
      accessorKey: 'reporting_years',
      id: 'reporting_years',
      size: 200,
      header: ({ column }) => {
        return h(DataTableFilterableHeader, {
          column,
          title: 'Reporting Year',
          filterData: (filterOptions.reporting_years || []).map(year => ({
            id: year.id || year.formatted_date,
            name: year.formatted_date,
          })),
          sort: false,
        })
      },
      cell: createReportingYearCell,
      enableSorting: false,
      enableColumnFilter: true,
    },
    {
      id: 'actions',
      size: 120,
      header: () => h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'Actions'),
      cell: ({ row }) => {
        return h('div', { class: 'flex items-center space-x-1' }, [
          h('button', {
            onClick: () => onDeleteRow && onDeleteRow(row.original.id),
            title: 'Delete',
            class: 'text-red-500 p-1 hover:bg-slate-200 rounded-md',
          }, [
            h(TrashGreenIcon, { size: 16 }),
          ]),
          h(EditGhgEmissionFactorModal, {
            preconfigEf: row.original,
            onEfConfigEdited: refresh,
          }),
        ])
      },
      enableSorting: false,
      enableColumnFilter: false,
    },
  ] as ColumnDef<PreconfiguredEmissionFactor>[]
}
